import {
  Listing,
  ListingBroadcastStatus,
  ListingDetails,
  ListingStatus,
  ListingStatusInfo,
  Marketplace,
  MarketplaceListing,
  MarketplaceListingStatusInfo,
  QualityControlState,
} from 'src/WebApiController';

import { MarketplaceFailure } from './useGetMarketplaceFailures';

export type MarketplaceDisplayProps = {
  marketplaceListing: MarketplaceListing;
  formIndex: number;
  status: ListingStatus;
  setListingStatus: (
    marketplace: Marketplace,
    newListingStatus: ListingStatus
  ) => void;
  isUKEvent?: boolean;
  isViagogo?: boolean;
  isDeleted?: boolean;
  disabled?: boolean;
  onListingBroadcastToggle: (
    isBroadcasting: boolean,
    marketplace: Marketplace
  ) => void;
  error?: MarketplaceFailure | null;
  qcState?: QualityControlState | null;
};

const getNextMarketplaceStatuses = (
  newListing: ListingStatusInfo,
  marketplaceListingStatus: Record<Marketplace, ListingStatus>
) => {
  const newMarketplaceStatuses = {} as Record<Marketplace, ListingStatus>;

  // We we get back a listing from server, it may or may not have finished the job
  // So we just have to assume the status is flowing in one direct only, ie, if we set something to pending,
  // we expect it to either finish or failed (but not reversed)
  const newMarketplaceListings = newListing.mkpListings;
  newMarketplaceListings.forEach((ml) => {
    if (ml.mkp) {
      if (
        marketplaceListingStatus[ml.mkp!] === ListingStatus.DelistingPending
      ) {
        // If we are delisting-pending, set if the new status is either Delisted or DelistingFailed
        if (
          ml.status === ListingStatus.Disabled ||
          ml.status === ListingStatus.Delisted ||
          ml.status === ListingStatus.DelistingFailed
        ) {
          newMarketplaceStatuses[ml.mkp] = ml.status;
        }
      } else if (
        marketplaceListingStatus[ml.mkp!] === ListingStatus.ListingPending
      ) {
        // If we are listing-pending, set if the new status is either Listed or ListedFailed
        if (
          ml.status === ListingStatus.Disabled ||
          ml.status === ListingStatus.Listed ||
          ml.status === ListingStatus.ListingFailed
        ) {
          newMarketplaceStatuses[ml.mkp] = ml.status;
        }
      }
    }
  });

  return newMarketplaceStatuses;
};

export const hasPendingMarketplaces = (
  marketplaceListingStatus: Record<Marketplace, ListingStatus>
) =>
  Object.keys(marketplaceListingStatus).some(
    (marketplace) =>
      marketplaceListingStatus[marketplace as Marketplace] ===
        ListingStatus.ListingPending ||
      marketplaceListingStatus[marketplace as Marketplace] ===
        ListingStatus.DelistingPending
  );

export const updateMarketplaceStatusesAndListing = (
  interval: NodeJS.Timeout,
  listing: Listing,
  newListing: ListingStatusInfo,
  marketplaceListingStatus: Record<Marketplace, ListingStatus>,
  setMarketplaceListingStatus: React.Dispatch<
    React.SetStateAction<Record<Marketplace, ListingStatus>>
  >,
  onListingDetailsUpdate: (listing: ListingDetails) => void
) => {
  const newMarketplaceStatuses = getNextMarketplaceStatuses(
    newListing,
    marketplaceListingStatus
  );

  // Only if we do get status changes shall we try to actually update the active-item
  // provider with the latest states, or else the toggle will jump back and forth due
  // to the useEffect earlier (since we modified the active listing)
  if (Object.keys(newMarketplaceStatuses).length > 0) {
    const newStatus = {
      ...marketplaceListingStatus,
      ...newMarketplaceStatuses,
    };
    setMarketplaceListingStatus(newStatus);

    // Use the calculated status on the new listing obj
    newListing.mkpListings.forEach((ml) => {
      if (ml.mkp) {
        ml.status = newStatus[ml.mkp!];
      }
    });

    // Only stop running this if all the status is no longer pending
    clearInterval(interval);

    let updatedListing = newListing as ListingDetails;

    const newMkpListings = newListing.mkpListings.reduce(
      (r, c) => {
        r[c.mkp] = c;
        return r;
      },
      {} as Record<Marketplace, MarketplaceListingStatusInfo>
    );

    updatedListing = {
      ...listing,
      ...newListing,
      // for marketplaces, we also need to merge since the MarketplaceListingStatusInfo is just a subset of the MarketplaceListing
      mkpListings:
        listing.mkpListings?.map((mkp) => ({
          ...mkp,
          ...(newMkpListings[mkp.mkp] ?? {}),
        })) ?? null,
    } as ListingDetails;

    onListingDetailsUpdate(updatedListing);
  }
};

export const getListingBroadcastEnabled = (
  listing: Listing | null | undefined,
  ml: MarketplaceListing,
  status: ListingStatus,
  qcState: QualityControlState | null | undefined,
  isUKEvent: boolean | null | undefined,
  isViagogo: boolean | null | undefined,
  disabled: boolean | null | undefined,
  isDeleted: boolean | null | undefined
) => {
  const alwaysShowAsDelisted =
    isUKEvent && ml.mkp === Marketplace.StubHub && !isViagogo;
  const noListingQty = listing ? listing.availQty === 0 : true;

  const isChecked =
    !alwaysShowAsDelisted &&
    (status === ListingStatus.Listed ||
      status === ListingStatus.ListingPending ||
      status === ListingStatus.DelistingFailed ||
      ml.brdcstStatus === ListingBroadcastStatus.BroadcastWithError);

  const hasBroadcastErrors =
    ml.status === ListingStatus.ListingFailed ||
    ml.status === ListingStatus.DelistingFailed;

  const disabledDueToPendingQC =
    ml.status !== ListingStatus.Listed &&
    qcState === QualityControlState.Pending;

  const broadcastChecked =
    noListingQty || listing?.isAdminHold ? false : isChecked;
  const broadcastDisabled =
    disabled ||
    isDeleted ||
    noListingQty ||
    (!isChecked && !(ml.isEnabled ?? true)) ||
    disabledDueToPendingQC ||
    listing?.isAdminHold;

  return {
    broadcastChecked,
    broadcastDisabled,
    alwaysShowAsDelisted,
    disabledDueToPendingQC,
    hasBroadcastErrors,
  };
};
